import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { MembershipManagmentB2BService } from '@sdk-point/talisis';

const MembershipManagmentB2BServiceSDK = new MembershipManagmentB2BService(process.env.REACT_APP_ENV_V2, process.env.REACT_APP_X_API_KEY_V2);

// Operation types understood by the SDK orchestrator (POST /users/b2b/modifications).
// A single endpoint handles the three processes based on `operationType` + `operationData`.
const OPERATION = {
    CANCELLATION: 'baja',
    EXTENSION: 'extension',
    MEMBERSHIP_CHANGE: 'cambio_membresia',
    RESEND_EMAIL: 'reenvio_correo',
};

// Map the raw student object (as returned by UserService.getAll) to the shape the
// B2BModificationBody.student contract expects. The source object uses different
// field names across the module, so we normalize here in one place.
const toContractStudent = (student) => ({
    personId: Number(student.person_id ?? student.id),
    name: student.complete_name
        || student.full_name
        || `${student.first_name || student.name || ''} ${student.last_name || ''}`.trim(),
    email: student.email,
    // El buscador (UserService.getAll) devuelve el código de empresa en `company_id`
    // (ej. "TAL"). Ese es el valor que la modificación registra como empresa del
    // alumno; alimenta lmb.company_id en el log de modificaciones.
    companyId: student.company_id || '',
    currentMembership: student.membership_name || '',
});

export default function useModificationActions() {
    // Logged-in admin performing the operation. The modifications contract requires
    // requestedBy (person_id) and requestedByName so the backend can audit who did it.
    const loggedUser = useSelector((state) => state.userReducer?.user) || {};

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const clearResult = useCallback(() => setResult(null), []);

    // Single entry point: builds the B2BModificationBody and calls the orchestrator.
    // Each public action (baja/extension/cambio) delegates here with its own
    // operationType + operationData, so the request/error handling lives in one place.
    const runModification = useCallback(async ({ operationType, student, operationData, errorFallback }) => {
        setLoading(true);
        setResult(null);
        try {
            // The orchestrator lambda rejects (400) a requestedBy that is not a
            // positive integer and a requestedByName that is empty. Guard here so the
            // user gets a clear message instead of an opaque backend validation error.
            const requestedBy = Number(loggedUser.person_id);
            const requestedByName = (loggedUser.name
                || `${loggedUser.first_name || ''} ${loggedUser.last_name || ''}`.trim()
                || loggedUser.email
                || '').trim();

            if (!Number.isInteger(requestedBy) || requestedBy <= 0 || requestedByName.length === 0) {
                setResult({ type: 'error', message: 'No se pudo identificar al usuario en sesión. Vuelva a iniciar sesión e intente de nuevo.' });
                return;
            }

            const body = {
                operationType,
                requestedBy,
                requestedByName,
                student: toContractStudent(student),
                operationData,
            };

            const response = await MembershipManagmentB2BServiceSDK.postModification(body);

            if (response && response.success) {
                setResult({ type: 'success', message: response.message || 'Operación procesada exitosamente.' });
            } else {
                setResult({ type: 'error', message: (response && response.message) || errorFallback });
            }
            return response;
        } catch (err) {
            const message = (err && err.response && err.response.data && err.response.data.message)
                || (err && err.message) || (typeof err === 'string' ? err : errorFallback);
            setResult({ type: 'error', message });
        } finally {
            setLoading(false);
        }
    }, [loggedUser.person_id, loggedUser.name, loggedUser.first_name, loggedUser.last_name, loggedUser.email]);

    const processBaja = useCallback((student) => runModification({
        operationType: OPERATION.CANCELLATION,
        student,
        operationData: {
            reserved_timeout: 10000,
            cancel_reason: 'user_requested',
        },
        errorFallback: 'Error al procesar la baja.',
    }), [runModification]);

    const extendMembership = useCallback((student, newDate) => runModification({
        operationType: OPERATION.EXTENSION,
        student,
        operationData: {
            new_date: newDate,
        },
        errorFallback: 'Error al extender la membresía.',
    }), [runModification]);

    const changeMembership = useCallback(({ student, membershipSku, addonSkus, paymentReferenceId }) => runModification({
        operationType: OPERATION.MEMBERSHIP_CHANGE,
        student,
        operationData: {
            product_skus: { membership: membershipSku, addons: addonSkus || [] },
            payment_reference_id: paymentReferenceId,
            force_update_membership: true,
            email_code_to_use: 'PERS',
            reserved_timeout: null,
        },
        errorFallback: 'Error al cambiar la membresía.',
    }), [runModification]);

    // Reenvío de correo al alumno. El backend toma el destinatario de student.email
    // (ya normalizado por toContractStudent). operationData va vacío: el orquestador
    // no necesita datos extra para esta operación.
    const resendEmail = useCallback((student) => runModification({
        operationType: OPERATION.RESEND_EMAIL,
        student,
        operationData: {},
        errorFallback: 'Error al reenviar el correo.',
    }), [runModification]);

    return { loading, result, clearResult, processBaja, extendMembership, changeMembership, resendEmail };
}
